import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  FaQuestionCircle, 
  FaSearch, 
  FaChevronDown, 
  FaChevronUp,
  FaEnvelope,
  FaPhone,
  FaComments,
  FaBook,
  FaShieldAlt
} from 'react-icons/fa';

const Container = styled.div`
  min-height: 100vh;
  background-color: #000;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 48px;
`;

const Title = styled.h1`
  color: #fff;
  font-size: 36px;
  font-weight: bold;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  color: #ccc;
  font-size: 18px;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const SearchContainer = styled.div`
  margin-bottom: 48px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 16px 20px;
  background-color: #111;
  border: 1px solid #333;
  border-radius: 12px;
  color: #fff;
  font-size: 16px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }

  &::placeholder {
    color: #666;
  }
`;

const Section = styled.section`
  margin-bottom: 48px;
`;

const SectionTitle = styled.h2`
  color: #fff;
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const FAQContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FAQItem = styled.div`
  background-color: #111;
  border-radius: 12px;
  border: 1px solid #333;
  overflow: hidden;
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 20px;
  background: none;
  border: none;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #222;
  }
`;

const FAQAnswer = styled.div`
  padding: 0 20px;
  max-height: ${props => props.isOpen ? '200px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  color: #ccc;
  font-size: 14px;
  line-height: 1.6;
  border-top: ${props => props.isOpen ? '1px solid #333' : 'none'};
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-top: 24px;
`;

const ContactCard = styled.div`
  background-color: #111;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #333;
  text-align: center;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-4px);
  }
`;

const ContactIcon = styled.div`
  color: #4CAF50;
  font-size: 32px;
  margin-bottom: 16px;
`;

const ContactTitle = styled.h3`
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const ContactDescription = styled.p`
  color: #ccc;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 16px;
`;

const ContactButton = styled.button`
  background-color: #4CAF50;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #45a049;
  }
`;

const ResourcesSection = styled.div`
  background-color: #111;
  border-radius: 12px;
  padding: 32px;
  border: 1px solid #333;
`;

const ResourcesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 24px;
`;

const ResourceItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background-color: #222;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #333;
  }
`;

const ResourceIcon = styled.div`
  color: #4CAF50;
  font-size: 20px;
`;

const ResourceText = styled.div`
  color: #fff;
  font-size: 14px;
  font-weight: 500;
`;

const HelpPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqs = [
    {
      id: 1,
      question: "How do I create an account?",
      answer: "To create an account, click on the 'Register' button on the login page. Fill in your name, username, email, and password. Once you submit the form, you'll receive a confirmation email to verify your account."
    },
    {
      id: 2,
      question: "How do I reset my password?",
      answer: "If you've forgotten your password, click on the 'Forgot Password?' link on the login page. Enter your email address and we'll send you a link to reset your password."
    },
    {
      id: 3,
      question: "How do I post content?",
      answer: "To create a post, click on the 'Create Post' button in the navigation. You can add text, images, or videos to your post. Click 'Post' to share it with your followers."
    },
    {
      id: 4,
      question: "How do I follow other users?",
      answer: "You can find users to follow by using the search function or browsing through the feed. Click on a user's profile and then click the 'Follow' button to start following them."
    },
    {
      id: 5,
      question: "How do I report inappropriate content?",
      answer: "If you see inappropriate content, click on the three dots menu on the post and select 'Report'. Our team will review the content and take appropriate action."
    },
    {
      id: 6,
      question: "How do I change my profile settings?",
      answer: "Go to your profile page and click on the 'Edit Profile' button. You can update your profile picture, bio, and other information from there."
    },
    {
      id: 7,
      question: "How do I block a user?",
      answer: "To block a user, go to their profile page and click on the three dots menu. Select 'Block User' from the options. You can unblock them later from your settings."
    },
    {
      id: 8,
      question: "Is my data secure?",
      answer: "Yes, we take your privacy and security seriously. We use industry-standard encryption and security measures to protect your personal information and data."
    }
  ];

  const filteredFAQs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <Container>
      <Content>
        <Header>
          <Title>Help & Support</Title>
          <Subtitle>
            Find answers to common questions and get the help you need to make the most of Weloop.
          </Subtitle>
        </Header>

        <SearchContainer>
          <SearchInput
            type="text"
            placeholder="Search for help topics..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchContainer>

        <Section>
          <SectionTitle>
            <FaQuestionCircle />
            Frequently Asked Questions
          </SectionTitle>
          <FAQContainer>
            {filteredFAQs.map((faq) => (
              <FAQItem key={faq.id}>
                <FAQQuestion onClick={() => toggleFAQ(faq.id)}>
                  {faq.question}
                  {openFAQ === faq.id ? <FaChevronUp /> : <FaChevronDown />}
                </FAQQuestion>
                <FAQAnswer isOpen={openFAQ === faq.id}>
                  {faq.answer}
                </FAQAnswer>
              </FAQItem>
            ))}
          </FAQContainer>
        </Section>

        <Section>
          <SectionTitle>
            <FaEnvelope />
            Contact Support
          </SectionTitle>
          <ContactGrid>
            <ContactCard>
              <ContactIcon>
                <FaEnvelope />
              </ContactIcon>
              <ContactTitle>Email Support</ContactTitle>
              <ContactDescription>
                Get help via email. We typically respond within 24 hours.
              </ContactDescription>
              <ContactButton onClick={() => window.open('mailto:support@weloop.tech')}>
                Send Email
              </ContactButton>
            </ContactCard>

            <ContactCard>
              <ContactIcon>
                <FaComments />
              </ContactIcon>
              <ContactTitle>Live Chat</ContactTitle>
              <ContactDescription>
                Chat with our support team in real-time during business hours.
              </ContactDescription>
              <ContactButton>
                Start Chat
              </ContactButton>
            </ContactCard>

            <ContactCard>
              <ContactIcon>
                <FaPhone />
              </ContactIcon>
              <ContactTitle>Phone Support</ContactTitle>
              <ContactDescription>
                Call us directly for urgent issues. Available 9 AM - 6 PM EST.
              </ContactDescription>
              <ContactButton onClick={() => window.open('tel:+1-555-0123')}>
                Call Now
              </ContactButton>
            </ContactCard>
          </ContactGrid>
        </Section>

        <Section>
          <SectionTitle>
            <FaBook />
            Helpful Resources
          </SectionTitle>
          <ResourcesSection>
            <ResourcesGrid>
              <ResourceItem>
                <ResourceIcon>
                  <FaBook />
                </ResourceIcon>
                <ResourceText>User Guide</ResourceText>
              </ResourceItem>
              <ResourceItem>
                <ResourceIcon>
                  <FaShieldAlt />
                </ResourceIcon>
                <ResourceText>Privacy Policy</ResourceText>
              </ResourceItem>
              <ResourceItem>
                <ResourceIcon>
                  <FaBook />
                </ResourceIcon>
                <ResourceText>Terms of Service</ResourceText>
              </ResourceItem>
              <ResourceItem>
                <ResourceIcon>
                  <FaQuestionCircle />
                </ResourceIcon>
                <ResourceText>Community Guidelines</ResourceText>
              </ResourceItem>
            </ResourcesGrid>
          </ResourcesSection>
        </Section>
      </Content>
    </Container>
  );
};

export default HelpPage; 